import { ChevronDown, Clock, Luggage, Mail, MessageSquareText, Phone, TriangleAlert } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsList, TabsPanel, TabsTab } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import type {
  PnrDisplayFare,
  PnrDisplaySegment,
  PnrDisplayTicket as PnrDisplayTicketData,
  PnrDisplayTimelineEvent,
  PnrDisplayView,
} from "../../api/use-claim-pnr-display";
import { COUPON_STATUS_LABELS, formatNaiveDate, GENDER_LABELS } from "./pnr-itinerary-lib";

/**
 * Amadeus reports local times with no offset. We therefore only ever reformat the clock — never
 * convert, and never compute a duration across two airports (it would be wrong by the time
 * difference). The `+1` arrival marker is safe because it is a date difference, not a duration.
 */
function formatDateTime(value: string | null) {
  if (!value) {
    return "—";
  }

  const [date, time] = value.split("T");
  const [year, month, day] = date.split("-");

  return time ? `${day}/${month}/${year} à ${time}` : `${day}/${month}/${year}`;
}

/** Null for a date-only event: the source carried no time, and we do not make one up. */
function formatTime(value: string | null) {
  return value?.split("T")[1] ?? null;
}

/** « 1 034,00 € » — French grouping and decimal comma; an unknown currency keeps its code. */
function formatAmount(amount: number | null, currency: string | null) {
  if (amount === null) {
    return "—";
  }
  if (!currency) {
    return amount.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  try {
    return amount.toLocaleString("fr-FR", { style: "currency", currency });
  } catch {
    return `${amount.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
  }
}

function segmentLabel(segment: PnrDisplaySegment | undefined, tattoo: string) {
  return segment
    ? `${segment.carrier ?? ""}${segment.flightNumber ?? ""} ${segment.from ?? ""}–${segment.to ?? ""}`
    : `ST${tattoo}`;
}

function formatDate(value: string | null) {
  if (!value) {
    return "—";
  }

  const [year, month, day] = value.split("T")[0].split("-");

  return `${day}/${month}/${year}`;
}

/** A label over its value — whitespace does the grouping, no cell borders. */
function Field({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className={cn("text-sm break-words text-gray-900", mono && "font-mono")}>{children}</dd>
    </div>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return <h4 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{children}</h4>;
}

/**
 * The reprotection trail. The three GDS lines are shown verbatim under the summary: they are the
 * evidence that establishes the cancellation, so a jurist reads them unedited rather than trusting
 * our regex.
 */
export function PnrDisplayRerouting({ view }: { view: PnrDisplayView }) {
  const { rerouting, itinerary } = view;
  const segmentOf = (tattoo: string) => itinerary.find((segment) => segment.tattoo === tattoo);
  const scheduleChanged = rerouting.signals.scheduleChangedSegmentTattoos
    .map((tattoo) => segmentLabel(segmentOf(tattoo), tattoo))
    .join(", ");
  const removed = rerouting.signals.removedSegmentTattoos;

  if (!rerouting.detected) {
    if (!scheduleChanged) {
      return null;
    }

    return (
      <Alert variant="warning">
        <Clock aria-hidden="true" />
        <AlertTitle>Changement d'horaire par la compagnie</AlertTitle>
        <AlertDescription>
          <span className="text-foreground">
            Passé par le statut TK (horaire modifié par la compagnie) : {scheduleChanged}.
          </span>
        </AlertDescription>
      </Alert>
    );
  }

  const facts = [
    ...rerouting.signals.airlineBookedSegmentTattoos.map((tattoo) => {
      const segment = segmentOf(tattoo);
      return `${segmentLabel(segment, tattoo)} ajouté au dossier par la compagnie${segment?.bookedByOffice ? ` (bureau ${segment.bookedByOffice})` : ""}${segment?.bookedAt ? ` le ${formatDateTime(segment.bookedAt)}` : ""}`;
    }),
    scheduleChanged ? `Horaire modifié par la compagnie (TK) : ${scheduleChanged}` : null,
    removed.length > 0
      ? `${removed.length > 1 ? "Segments" : "Segment"} ${removed.map((tattoo) => `ST${tattoo}`).join(", ")} ${removed.length > 1 ? "supprimés" : "supprimé"} du dossier`
      : null,
  ].filter((fact) => fact !== null);

  return (
    <Alert variant="warning">
      <TriangleAlert aria-hidden="true" />
      <AlertTitle>Réacheminement involontaire</AlertTitle>
      <AlertDescription>
        <span className="text-foreground">
          {rerouting.cancelledFlight
            ? `Le vol ${rerouting.cancelledFlight} a été annulé par la compagnie ; le passager a été réacheminé.`
            : "La compagnie a réacheminé le passager de façon involontaire."}
        </span>
        {facts.length > 0 ? (
          <ul className="list-disc pl-4 text-foreground">
            {facts.map((fact) => (
              <li key={fact}>{fact}</li>
            ))}
          </ul>
        ) : null}
        <div className="flex flex-col gap-1">
          {rerouting.sources.map((source) => (
            <div
              key={`${source.tag}-${source.text}`}
              className="grid grid-cols-[2.5rem_1fr] items-start gap-2 rounded-md border border-warning/24 bg-background px-2 py-1 font-mono text-xs"
            >
              <span className="font-semibold text-muted-foreground">{source.tag}</span>
              <span className="break-words text-foreground">{source.text}</span>
            </div>
          ))}
        </div>
      </AlertDescription>
    </Alert>
  );
}

const TONE_DOT: Record<PnrDisplayTimelineEvent["tone"], string> = {
  neutral: "bg-gray-400",
  accent: "bg-blue-500",
  warn: "bg-amber-500",
  critical: "bg-red-500",
};

export function PnrDisplayTimeline({ view }: { view: PnrDisplayView }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xs tracking-wide text-gray-500 uppercase">Chronologie</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="flex flex-col">
          {view.timeline.map((event, index) => (
            <li key={`${event.at}-${event.label}-${index}`} className="grid grid-cols-[6rem_1rem_1fr] gap-2">
              <span className="pt-2 text-right text-xs text-gray-500 tabular-nums">
                {formatNaiveDate(event.at)}
                {formatTime(event.at) ? (
                  <span className="block text-xs text-gray-400">{formatTime(event.at)}</span>
                ) : null}
              </span>

              <span className="relative flex justify-center">
                <span className="absolute inset-y-0 w-px bg-gray-200" aria-hidden />
                <span
                  className={cn(
                    "relative mt-2.5 h-2 w-2 rounded-full ring-2 ring-white",
                    TONE_DOT[event.tone],
                  )}
                />
              </span>

              <div className="min-w-0 py-2">
                <p className="text-sm text-gray-900">
                  {event.label}
                  <span className="ml-2 text-xs tracking-wide text-gray-400 uppercase">{event.kind}</span>
                </p>
                {event.raw ? (
                  <p className="mt-0.5 font-mono text-xs break-words text-gray-400">{event.raw}</p>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

/** `0 PC` → « Aucun bagage en soute », `2 PC` → « 2 bagages en soute », `20 K` → « 20 K ». */
function formatFreeBaggage(quantity: number | null, unit: string | null) {
  if (quantity === null) {
    return null;
  }
  if (unit !== "PC") {
    return `${quantity} ${unit ?? ""}`.trim();
  }

  return quantity === 0 ? "Aucun bagage en soute" : `${quantity} bagage${quantity > 1 ? "s" : ""} en soute`;
}

/** Coupon statuses read by their consequence: flown is settled, refunded or voided is gone. */
const COUPON_STATUS_TONES: Record<string, "success" | "error" | "warning" | "outline"> = {
  B: "success",
  V: "error",
  R: "error",
  E: "warning",
  S: "warning",
};

function FareSummary({ fare }: { fare: PnrDisplayFare }) {
  return (
    <dl className="flex flex-col text-sm">
      <div className="flex justify-between gap-4 py-1.5">
        <dt className="text-gray-600">Tarif</dt>
        <dd className="text-gray-900 tabular-nums">
          {fare.isInclusiveTour ? "Masqué (IT)" : formatAmount(fare.baseFare, fare.currency)}
        </dd>
      </div>
      <div className="flex justify-between gap-4 py-1.5">
        <dt className="text-gray-600">Taxes</dt>
        <dd className="text-gray-900 tabular-nums">{formatAmount(fare.taxTotal, fare.currency)}</dd>
      </div>
      {fare.carrierSurchargeTotal > 0 ? (
        <div className="flex justify-between gap-4 pb-1.5 pl-3 text-xs text-gray-500">
          <dt>dont surcharges transporteur (YQ/YR)</dt>
          <dd className="tabular-nums">{formatAmount(fare.carrierSurchargeTotal, fare.currency)}</dd>
        </div>
      ) : null}
      <div className="mt-1 flex justify-between gap-4 border-t border-gray-200 pt-2.5 pb-1">
        <dt className="font-semibold text-gray-900">Total billet</dt>
        <dd className="font-semibold text-gray-900 tabular-nums">
          {formatAmount(fare.totalFare, fare.currency)}
        </dd>
      </div>
      {fare.grandTotal !== null && fare.grandTotal !== fare.totalFare ? (
        <div className="flex justify-between gap-4 py-1">
          <dt className="text-gray-600">Total avec frais</dt>
          <dd className="font-semibold text-gray-900 tabular-nums">
            {formatAmount(fare.grandTotal, fare.currency)}
          </dd>
        </div>
      ) : null}
    </dl>
  );
}

function PassengerTicket({
  ticket,
  fares,
  view,
}: {
  ticket: PnrDisplayTicketData | null;
  fares: PnrDisplayFare[];
  view: PnrDisplayView;
}) {
  if (!ticket && fares.length === 0) {
    return <p className="text-sm text-gray-500">Aucun billet émis pour ce passager.</p>;
  }

  const { ticketedOn } = view.dossier;
  const segmentOf = (tattoo: string | null) => view.itinerary.find((flight) => flight.tattoo === tattoo);

  return (
    <div className="flex flex-col gap-6">
      {ticket ? (
        <section className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="font-mono text-base font-semibold tracking-tight text-gray-900">
              {ticket.airlineCode ?? ""}-{ticket.number ?? ""}
            </span>
            <Badge variant="outline" size="sm">
              {ticket.isElectronic ? "Électronique" : "Papier"}
            </Badge>
          </div>

          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <Field label="Émis le">{formatNaiveDate(ticket.issuedOn, { year: true })}</Field>
            {ticketedOn && ticketedOn !== ticket.issuedOn ? (
              <Field label="Première émission">{formatNaiveDate(ticketedOn, { year: true })}</Field>
            ) : null}
            <Field label="Transporteur validant" mono>
              {ticket.validatingCarrier ?? "—"}
            </Field>
            <Field label="Bureau émetteur" mono>
              {ticket.office ?? "—"}
            </Field>
          </dl>

          {ticket.coupons.length > 0 ? (
            <div className="flex flex-col gap-2">
              <SubHeading>Coupons</SubHeading>
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {ticket.coupons.map((coupon) => (
                  <li
                    key={coupon.number ?? coupon.segment}
                    className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                  >
                    <span className="text-sm text-gray-900">
                      <span className="text-gray-500">Coupon {coupon.number}</span>
                      {coupon.segment ? (
                        <span className="ml-2 font-medium">
                          {segmentLabel(segmentOf(coupon.segment), coupon.segment)}
                        </span>
                      ) : null}
                    </span>
                    {coupon.status ? (
                      <Badge variant={COUPON_STATUS_TONES[coupon.status] ?? "outline"} size="sm">
                        {COUPON_STATUS_LABELS[coupon.status] ?? "statut"}
                        <span className="font-mono opacity-60">{coupon.status}</span>
                      </Badge>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      ) : null}

      {fares.map((fare) => (
        <section
          key={fare.tstNumber ?? fare.fareCalculation ?? "fare"}
          className="flex flex-col gap-4 border-t border-gray-100 pt-5"
        >
          <div className="flex items-baseline justify-between gap-2">
            <SubHeading>Tarif</SubHeading>
            {fare.tstNumber ? (
              <span className="font-mono text-xs text-gray-400">TST {fare.tstNumber}</span>
            ) : null}
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <FareSummary fare={fare} />

            {fare.segments.length > 0 ? (
              <ul className="flex flex-col gap-2 self-start">
                {fare.segments.map((segment) => {
                  const baggage = formatFreeBaggage(segment.freeBaggageQuantity, segment.freeBaggageUnit);
                  return (
                    <li
                      key={`${segment.segmentTattoo}-${segment.fareBasis}`}
                      className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg bg-gray-50 px-3 py-2 text-sm"
                    >
                      <span className="flex flex-col">
                        <span className="font-medium text-gray-900">
                          {segmentLabel(segmentOf(segment.segmentTattoo), segment.segmentTattoo ?? "?")}
                        </span>
                        {segment.fareBasis ? (
                          <span className="font-mono text-xs text-gray-500">{segment.fareBasis}</span>
                        ) : null}
                      </span>
                      {baggage ? (
                        <span className="flex items-center gap-1.5 text-gray-600">
                          <Luggage className="size-3.5 text-gray-400" aria-hidden />
                          {baggage}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>

          {fare.isInclusiveTour ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Tarif <strong>Inclusive Tour</strong> (<code className="font-mono">M/IT</code> au calcul
              tarifaire) : le montant du billet est masqué et s'affiche à 0,00 €. Les{" "}
              {formatAmount(fare.taxTotal, fare.currency)} de taxes ne sont donc <strong>pas</strong> le prix
              payé par le passager.
            </p>
          ) : null}

          {fare.taxes.length > 0 ? (
            <Collapsible className="rounded-lg border border-gray-200">
              <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
                <span>
                  Détail des taxes{" "}
                  <span className="text-gray-400 tabular-nums">
                    · {fare.taxes.length} taxe{fare.taxes.length > 1 ? "s" : ""}
                  </span>
                </span>
                <ChevronDown
                  className="size-4 text-gray-400 transition-transform group-data-panel-open:rotate-180"
                  aria-hidden
                />
              </CollapsibleTrigger>
              <CollapsiblePanel>
                {/* Codes IATA bruts : on n'invente pas de libellé pour O4, QX, QZ, GP… */}
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 border-t border-gray-100 px-3 py-3 text-sm sm:grid-cols-3 lg:grid-cols-4">
                  {fare.taxes.map((tax, index) => (
                    <div key={`${tax.code}-${index}`} className="flex justify-between gap-3">
                      <dt className="font-mono text-gray-500">{tax.code ?? "—"}</dt>
                      <dd className="text-gray-900 tabular-nums">{formatAmount(tax.amount, tax.currency)}</dd>
                    </div>
                  ))}
                </dl>
              </CollapsiblePanel>
            </Collapsible>
          ) : null}

          {fare.fareCalculation ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-gray-500">Calcul tarifaire</span>
              <pre className="overflow-x-auto rounded-lg bg-gray-50 px-3 py-2 font-mono text-xs whitespace-pre-wrap text-gray-700">
                {fare.fareCalculation}
              </pre>
            </div>
          ) : null}
        </section>
      ))}
    </div>
  );
}

/** One tab per passenger: each `FA` ticket and each TST reference the passengers they belong to. */
export function PnrDisplayTicket({ view }: { view: PnrDisplayView }) {
  if (view.tickets.length === 0 && view.fares.length === 0) {
    return null;
  }

  const entries = view.passengers.map((passenger, index) => ({
    key: passenger.tattoo ?? String(index),
    passenger,
    ticket: view.tickets.find((ticket) => ticket.passengerTattoo === passenger.tattoo) ?? null,
    fares: view.fares.filter(
      (fare) => passenger.tattoo !== null && fare.passengerTattoos.includes(passenger.tattoo),
    ),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xs tracking-wide text-gray-500 uppercase">Billet et tarif</CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length <= 1 ? (
          // A lone passenger owns every element, referenced or not.
          <PassengerTicket ticket={view.tickets[0] ?? null} fares={view.fares} view={view} />
        ) : (
          <Tabs defaultValue={entries[0].key}>
            <TabsList variant="underline">
              {entries.map((entry) => (
                <TabsTab key={entry.key} value={entry.key}>
                  {entry.passenger.raw}
                </TabsTab>
              ))}
            </TabsList>
            {entries.map((entry) => (
              <TabsPanel key={entry.key} value={entry.key} className="pt-4">
                <PassengerTicket ticket={entry.ticket} fares={entry.fares} view={view} />
              </TabsPanel>
            ))}
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}

const PASSENGER_TYPE_LABELS: Record<string, string> = {
  ADT: "Adulte",
  CHD: "Enfant",
  INF: "Bébé",
  YTH: "Jeune",
  SRC: "Senior",
  STU: "Étudiant",
};

const TITLE_LABELS: Record<string, string> = { MR: "M.", MRS: "Mme", MS: "Mme", MISS: "Mlle", MSTR: "M." };

function capitalizeWords(value: string): string {
  return value
    .toLowerCase()
    .replace(
      /(^|[\s-])(\p{L})/gu,
      (_match, separator: string, letter: string) => separator + letter.toUpperCase(),
    );
}

/** `PAX*mail@x.fr` / `ARR*mail@x.fr` — the Amadeus contact prefixes, read as who the address reaches. */
const CONTACT_ROLE_PREFIXES: Record<string, string> = { PAX: "passager", ARR: "organisateur" };

type ContactItem = PnrDisplayView["contacts"][number];

function readContact(contact: ContactItem) {
  const prefix = contact.value.match(/^([A-Z]{3})\*(.+)$/);
  const role = prefix && CONTACT_ROLE_PREFIXES[prefix[1]] ? CONTACT_ROLE_PREFIXES[prefix[1]] : null;
  const raw = role && prefix ? prefix[2] : contact.value;

  if (contact.kind === "email") {
    return { kind: "email" as const, display: raw.toLowerCase(), role, mobile: false };
  }
  // An `AP` line without a number is free text the agent typed there, not a phone.
  if ((raw.match(/\d/g) ?? []).length < 6) {
    return { kind: "note" as const, display: raw.replace(/^\*+|\*+$/g, "").trim(), role, mobile: false };
  }
  const mobile = /-M$/.test(raw);
  return { kind: "phone" as const, display: raw.replace(/-M$/, ""), role, mobile };
}

function ContactList({ title, contacts }: { title: string; contacts: ContactItem[] }) {
  if (contacts.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <SubHeading>{title}</SubHeading>
      <ul className="flex flex-col gap-2.5">
        {contacts.map((contact, index) => {
          const read = readContact(contact);
          const Icon = read.kind === "email" ? Mail : read.kind === "phone" ? Phone : MessageSquareText;
          const tags = [read.mobile ? "mobile" : null, read.role].filter(Boolean);

          return (
            <li key={`${contact.kind}-${contact.value}-${index}`} className="flex items-start gap-2.5">
              <Icon className="mt-0.5 size-4 shrink-0 text-gray-400" aria-hidden />
              <div className="min-w-0">
                <p
                  className={cn(
                    "text-sm break-all text-gray-900",
                    read.kind === "phone" && "tabular-nums",
                    read.kind === "note" && "text-gray-600 italic",
                  )}
                  title={contact.value}
                >
                  {read.display}
                </p>
                {tags.length > 0 || contact.sharedWithAirline ? (
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
                    {tags.length > 0 ? <span>{tags.join(" · ")}</span> : null}
                    {contact.sharedWithAirline ? (
                      <span className="text-emerald-700">Transmis à la compagnie</span>
                    ) : null}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function countryName(code: string): string {
  try {
    return new Intl.DisplayNames(["fr"], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

export function PnrDisplayPassengers({ view }: { view: PnrDisplayView }) {
  const passengerContacts = view.contacts.filter((contact) => contact.owner !== "agency");
  const agencyContacts = view.contacts.filter((contact) => contact.owner === "agency");
  const { dossier } = view;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xs tracking-wide text-gray-500 uppercase">Passager et contacts</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {view.passengers.map((passenger) => (
          <section key={passenger.tattoo ?? passenger.raw} className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <p className="text-base font-semibold text-gray-900">
                {passenger.title && TITLE_LABELS[passenger.title] ? `${TITLE_LABELS[passenger.title]} ` : ""}
                {capitalizeWords(passenger.firstName)} {passenger.surname.toUpperCase()}
              </p>
              {passenger.type ? (
                <Badge variant="outline" size="sm">
                  {PASSENGER_TYPE_LABELS[passenger.type] ?? passenger.type}
                </Badge>
              ) : null}
            </div>
            {/* The name exactly as the PNR holds it — what the airline will compare against. */}
            <p className="-mt-2 font-mono text-xs text-gray-500">{passenger.raw}</p>

            {passenger.birthDate || passenger.gender || passenger.uci ? (
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                {passenger.birthDate ? (
                  <Field label="Date de naissance">{formatDate(passenger.birthDate)}</Field>
                ) : null}
                {passenger.gender ? (
                  <Field label="Sexe">{GENDER_LABELS[passenger.gender] ?? passenger.gender}</Field>
                ) : null}
                {passenger.uci ? (
                  <Field label="UCI Amadeus" mono>
                    {passenger.uci}
                  </Field>
                ) : null}
              </dl>
            ) : null}
          </section>
        ))}

        {view.contacts.length > 0 ? (
          <div className="grid gap-6 border-t border-gray-100 pt-5 md:grid-cols-2">
            <ContactList title="Contacts passager" contacts={passengerContacts} />
            <ContactList title="Contacts agence" contacts={agencyContacts} />
          </div>
        ) : null}

        <div className="flex flex-col gap-3 border-t border-gray-100 pt-5">
          <SubHeading>Dossier Amadeus</SubHeading>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
            <Field label="Bureau responsable" mono>
              {dossier.responsibleOffice ?? "—"}
            </Field>
            {dossier.agencyIataCode ? (
              <Field label="IATA agence" mono>
                {dossier.agencyIataCode}
              </Field>
            ) : null}
            {dossier.creditOffice && dossier.creditOffice !== dossier.responsibleOffice ? (
              <Field label="Bureau de crédit" mono>
                {dossier.creditOffice}
              </Field>
            ) : null}
            {dossier.pointOfSaleCountry ? (
              <Field label="Pays de vente">{countryName(dossier.pointOfSaleCountry)}</Field>
            ) : null}
            <Field label="Dernière modification (GMT)">{formatDateTime(dossier.lastModifiedAt)}</Field>
            <Field label="Purge Amadeus">{formatDate(dossier.purgeDate)}</Field>
          </dl>
        </div>
      </CardContent>
    </Card>
  );
}

/** “Brut” view — regenerated from the same elements, so it cannot drift from the readable view. */
export function PnrDisplayRaw({ view }: { view: PnrDisplayView }) {
  const numbered = view.rawElements.filter((element) => element.line !== null);
  const unnumbered = view.rawElements.filter((element) => element.line === null);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-xs tracking-wide text-gray-500 uppercase">Éléments du dossier</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex flex-col font-mono text-xs">
            {numbered.map((element, index) => (
              <div
                key={`${element.line}-${element.name}-${index}`}
                className="grid grid-cols-[2rem_2.5rem_1fr] gap-2 px-4 py-1 odd:bg-gray-50"
              >
                <span className="text-right text-gray-400 tabular-nums">{element.line}</span>
                <span className="font-semibold text-blue-600">{element.name}</span>
                <span className="break-words whitespace-pre-wrap">{element.text}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {unnumbered.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-xs tracking-wide text-gray-500 uppercase">
              Éléments sans ligne
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="flex flex-col font-mono text-xs">
              {unnumbered.map((element, index) => (
                <div
                  key={`${element.name}-${index}`}
                  className="grid grid-cols-[2.5rem_1fr] gap-2 px-4 py-1 odd:bg-gray-50"
                >
                  <span className="font-semibold text-blue-600">{element.name}</span>
                  <span className="break-words whitespace-pre-wrap">{element.text}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

export { formatDateTime };
